import { useMemo } from "react";

import Dropdown from "~/components/shared/Dropdown";
import Button from "~/components/shared/Button";
import Icon from "~/components/shared/Icon";
import {
  parseTagParams,
  keyValueToTag,
  updateUrlTags,
  Tags,
} from "~/components/analytics/shared/tagHelpers";

const TAG_PARAM_NAMES = {
  filterBy: "tags",
  groupBy: "group_by",
};

const TagSelectors = ({ tags }) => {
  const baseUrl = new URL(window.location.href);
  const filterByTags = parseTagParams(baseUrl.searchParams.get(TAG_PARAM_NAMES.filterBy));

  const groupByTags: Tags = useMemo(() => {
    // The group_by param is expected to be in the format of `tag.key` to be consistent with
    // grouped_execution table in tests#index, runs#show, and build_tests.
    // Therefore we need to trim the `tag.` prefix from the param before parsing it into Tags.
    //
    // Currently, we only support grouping by a single tag, so we can simply trim the prefix and parse it as a single tag.
    // If we want to support grouping by multiple tags in the future, we will need to update the syntax of the group_by param
    // to allow for multiple tags (e.g. `tag.key1,tag.key2`) and update the parsing logic accordingly.
    const param = baseUrl.searchParams.get(TAG_PARAM_NAMES.groupBy);
    const tag = param?.startsWith("tag.") ? param.slice(4) : param;

    return parseTagParams(tag);
  }, [baseUrl.searchParams]);

  const linkClassNames =
    "no-underline p-1 text-xs text-charcoal-700 rounded focus:no-underline focus:text-charcoal-700 focus:outline focus:outline-2 focus:outline-lime-500 hover:text-purple-600 data-[active=true]:text-purple-600 hover:no-underline truncate max-w-full";

  // TODO: when we have multiple group_by tags, we can consolidate this with
  // the generateTagUrl function. Currently it is a bit different as it replaces
  // the existing param rather than adding to it.
  const generateGroupUrl = (tag) => {
    const url = new URL(window.location.href);

    // Remove the execution params so the drawer does not open
    url.searchParams.delete("execution_id");

    // Only supports adding a single group_by at the moment
    if (groupByTags.hasTag(tag)) {
      url.searchParams.delete(TAG_PARAM_NAMES.groupBy);
    } else {
      url.searchParams.set(TAG_PARAM_NAMES.groupBy, `tag.${tag}`);
    }

    return url.toString();
  };

  return (
    <div className="gap1 flex w-full min-w-0 flex-1 flex-wrap items-center">
      {tags.map(({ key, value }) => {
        const tag = keyValueToTag(key, value);
        const activeFilter = filterByTags.hasTag(tag);
        const activeGroup = groupByTags.hasTag(key);

        return (
          <Dropdown key={tag} className="flex max-w-full">
            <Button
              theme="small"
              data-active={activeFilter || activeGroup}
              className="h-auto max-w-full px-2 py-0.5 text-xs data-[active=true]:text-purple-600"
            >
              <span className="max-w-full truncate">{tag}</span>
              <Icon
                icon="down-triangle"
                className="shrink-0"
                style={{ width: 8, height: 8, marginLeft: "5px" }}
              />
            </Button>

            <div className="flex flex-col gap-1 p-2">
              <a
                href={updateUrlTags({
                  key,
                  value,
                  currentTags: filterByTags,
                })}
                data-active={activeFilter}
                className={linkClassNames}
              >
                {activeFilter ? "Remove filter: " : "Filter by: "}
                <strong>{tag}</strong>
              </a>
              <a href={generateGroupUrl(key)} data-active={activeGroup} className={linkClassNames}>
                {activeGroup ? "Remove group: " : "Group by: "}
                <strong>{key}</strong>
              </a>
            </div>
          </Dropdown>
        );
      })}
    </div>
  );
};

export default TagSelectors;
